// src/features/files/components/AccessDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { filesAPI } from '../services/files';
import { foldersAPI } from '../services/folders';
import { rolesAPI } from '../../roles/services/roles';
import { staffAPI } from '../../staff/services/staff';
import { usePermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { getFolderName } from '../utils/getFolderName';

const PRINCIPALS = ['everyone', 'role', 'staff', 'permission'];
const EMPTY_RULE = { effect: 'allow', principal_type: 'role', principal_value: null, access: 'read' };

/** The name of whoever a rule is about, from the department's roles and staff */
function usePrincipalNames(open) {
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    if (!open) return;
    // Either list may be closed to the user; the rule then shows the ID
    rolesAPI.getDepartmentRoles().then(r => setRoles(r.data?.roles || [])).catch(() => setRoles([]));
    staffAPI.getDepartmentStaff().then(r => setStaff(r.data?.staff_members || [])).catch(() => setStaff([]));
  }, [open]);
  return { roles, staff };
}

/** Deny READ refuses everything, Deny WRITE only changes; the label says which */
function rightLabel(t, effect, access) {
  return effect === 'deny' ? t(`files:access.right.deny_${access}`) : t(`files:access.right.${access}`);
}

function principalLabel(t, rule, { roles, staff }) {
  const value = rule.principal_value;
  switch (rule.principal_type) {
    case 'everyone':
      return t('files:access.principal.everyone');
    case 'role':
      return `${t('files:access.principal.role')}: ${roles.find(r => r.role_id === value)?.name ?? value}`;
    case 'staff':
      return `${t('files:access.principal.staff')}: ${staff.find(s => s.staff_member_id === value)?.name ?? value}`;
    case 'permission':
      return `${t('files:access.principal.permission')}: ${value}`;
    default:
      return value;
  }
}

/** One own rule: effect, who, right, and moving it up and down */
function RuleRow({ rule, index, count, names, scopes, onChange, onMove, onRemove }) {
  const { t } = useTranslation(['files']);
  const set = (patch) => onChange({ ...rule, ...patch });
  const { roles, staff } = names;

  let valuePicker = null;
  if (rule.principal_type === 'role') {
    valuePicker = (
      <Autocomplete
        size="small"
        options={roles}
        getOptionLabel={(r) => r.name}
        value={roles.find(r => r.role_id === rule.principal_value) || null}
        onChange={(_, r) => set({ principal_value: r?.role_id ?? null })}
        renderInput={(params) => <TextField {...params} label={t('files:access.principal.role')} />}
        sx={{ flex: 1, minWidth: 180 }}
      />
    );
  } else if (rule.principal_type === 'staff') {
    valuePicker = (
      <Autocomplete
        size="small"
        options={staff}
        getOptionLabel={(s) => s.name}
        value={staff.find(s => s.staff_member_id === rule.principal_value) || null}
        onChange={(_, s) => set({ principal_value: s?.staff_member_id ?? null })}
        renderInput={(params) => <TextField {...params} label={t('files:access.principal.staff')} />}
        sx={{ flex: 1, minWidth: 180 }}
      />
    );
  } else if (rule.principal_type === 'permission') {
    valuePicker = (
      <Autocomplete
        size="small"
        freeSolo
        options={scopes}
        value={rule.principal_value || ''}
        onInputChange={(_, value) => set({ principal_value: value || null })}
        renderInput={(params) => (
          <TextField {...params} label={t('files:access.principal.permission')} placeholder="staff:update" />
        )}
        sx={{ flex: 1, minWidth: 180 }}
      />
    );
  } else {
    valuePicker = <Box sx={{ flex: 1, minWidth: 180 }} />;
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center" data-testid="access-rule">
      <Typography variant="body2" color="text.secondary" sx={{ width: 20, textAlign: 'right' }}>{index + 1}.</Typography>
      <TextField
        select
        size="small"
        label={t('files:access.effect.label')}
        value={rule.effect}
        onChange={(e) => set({ effect: e.target.value })}
        sx={{ width: 120 }}
      >
        <MenuItem value="allow">{t('files:access.effect.allow')}</MenuItem>
        <MenuItem value="deny">{t('files:access.effect.deny')}</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        label={t('files:access.who')}
        value={rule.principal_type}
        onChange={(e) => set({ principal_type: e.target.value, principal_value: null })}
        sx={{ width: 150 }}
      >
        {PRINCIPALS.map(p => <MenuItem key={p} value={p}>{t(`files:access.principal.${p}`)}</MenuItem>)}
      </TextField>
      {valuePicker}
      <TextField
        select
        size="small"
        label={t('files:access.right.label')}
        value={rule.access}
        onChange={(e) => set({ access: e.target.value })}
        sx={{ width: 220 }}
      >
        <MenuItem value="read">{rightLabel(t, rule.effect, 'read')}</MenuItem>
        <MenuItem value="write">{rightLabel(t, rule.effect, 'write')}</MenuItem>
      </TextField>
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <Tooltip title={t('files:access.move_up')}>
          <span>
            <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label={t('files:access.move_up')}>
              <UpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('files:access.move_down')}>
          <span>
            <IconButton size="small" disabled={index === count - 1} onClick={() => onMove(index, 1)} aria-label={t('files:access.move_down')}>
              <DownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('files:access.remove_rule')}>
          <IconButton size="small" color="error" onClick={() => onRemove(index)} aria-label={t('files:access.remove_rule')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );
}

/**
 * Who may see and change a file or folder: an ordered list of Allow/Deny rules,
 * read from the top, where the first rule about a person decides. A list with
 * Allow rules lets in only who they name; one with only Deny rules shuts out
 * who it names. The folders above apply too, so an item's rules can only narrow
 * access. People without access don't see the item at all. Needs file:permissions.
 *
 * `target` is { type: 'folder' | 'file', item }.
 */
export default function AccessDialog({ target, onClose, onSaved }) {
  const { t } = useTranslation(['files', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { scopes } = usePermissions();
  const open = Boolean(target);
  const names = usePrincipalNames(open);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([]);
  const [inherited, setInherited] = useState([]);

  const isFolder = target?.type === 'folder';
  const id = isFolder ? target?.item?.folder_id : target?.item?.file_id;
  const name = isFolder ? getFolderName(target?.item?.folder_name || '') : target?.item?.filename;
  const api = isFolder ? foldersAPI : filesAPI;

  useEffect(() => {
    if (!open || !id) return;
    setLoading(true);
    setError(null);
    api.getAccess(id)
      .then((res) => {
        setRules(res.data.rules);
        setInherited(res.data.inherited);
      })
      .catch(() => setError(t('files:access.load_failed')))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const scopeOptions = useMemo(() => [...new Set(scopes.filter(s => !s.includes('*')))].sort(), [scopes]);
  const incomplete = rules.some(r => r.principal_type !== 'everyone' && !r.principal_value);

  const move = (index, step) => setRules((list) => {
    const next = [...list];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.setAccess(id, { rules });
      showSuccess(t('files:access.saved', { name }));
      onSaved?.();
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('files:access.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('files:access.title', { name })}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack spacing={4} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('files:access.how_it_works')}</Typography>

            <Box>
              <Typography variant="subtitle2" gutterBottom>{t('files:access.own_rules')}</Typography>
              {rules.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('files:access.no_rules')}
                </Typography>
              ) : (
                <Stack spacing={3} sx={{ mb: 3, mt: 2 }}>
                  {rules.map((rule, index) => (
                    <RuleRow
                      // Rules have no ID until saved; position is their identity here
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      rule={rule}
                      index={index}
                      count={rules.length}
                      names={names}
                      scopes={scopeOptions}
                      onChange={(changed) => setRules(list => list.map((r, i) => (i === index ? changed : r)))}
                      onMove={move}
                      onRemove={(i) => setRules(list => list.filter((_, j) => j !== i))}
                    />
                  ))}
                </Stack>
              )}
              <Button size="small" startIcon={<AddIcon />} onClick={() => setRules(list => [...list, { ...EMPTY_RULE }])}>
                {t('files:access.add_rule')}
              </Button>
            </Box>

            {/* The folders' rules apply too; the item's own can only narrow them */}
            {inherited.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('files:access.parent_rules')}</Typography>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {inherited.map((source) => (
                    <Paper key={source.folder_id} variant="outlined" sx={{ p: 3, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('files:access.from_folder', { name: getFolderName(source.folder_name) })}
                      </Typography>
                      {source.rules.map((rule, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Typography key={i} variant="body2" color="text.secondary">
                          {i + 1}. {t(`files:access.effect.${rule.effect}`)} · {principalLabel(t, rule, names)} · {rightLabel(t, rule.effect, rule.access)}
                        </Typography>
                      ))}
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            <Alert severity="info">{t('files:access.managers_note')}</Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving || Boolean(error) || incomplete}>
          {saving ? t('common:buttons.saving') : t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
